import { useEffect, useMemo, useState } from "react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";

/**
 * Reusable "Start a new chat" dialog.
 *  - `mode = dm`     → pick exactly one teammate.
 *  - `mode = group`  → name the group + pick 1+ teammates.
 * Calls `onCreated(thread)` with the newly-created (or reused) thread.
 *
 * Used by both:
 *   - Chat page (full-page conversation view)
 *   - FloatingChatBubble (always-on bottom-right launcher)
 */
export default function NewChatDialog({ open, onOpenChange, onCreated }) {
  const { user } = useAuth();
  const [mode, setMode] = useState("dm");
  const [people, setPeople] = useState([]);
  const [selected, setSelected] = useState([]);
  const [name, setName] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setMode("dm"); setSelected([]); setName(""); setQ(""); return; }
    (async () => {
      try {
        const { data } = await api.get("/employees-lite");
        const list = (data || []).filter((u) => u.id !== user?.id);
        setPeople(list);
      } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    })();
  }, [open, user?.id]);

  const filteredPeople = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return people;
    return people.filter((p) => (p.name || "").toLowerCase().includes(s) || (p.email || "").toLowerCase().includes(s));
  }, [people, q]);

  const togglePerson = (uid) => {
    if (mode === "dm") setSelected([uid]);
    else setSelected((s) => s.includes(uid) ? s.filter((x) => x !== uid) : [...s, uid]);
  };

  const submit = async () => {
    if (selected.length === 0) { toast.error("Pick at least one person"); return; }
    if (mode === "group" && !name.trim()) { toast.error("Give the group a name"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/chat/threads", { type: mode, name: mode === "group" ? name.trim() : "", member_ids: selected });
      toast.success(mode === "dm" ? "Chat ready" : "Group created");
      onCreated?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="chat-new-dialog">
        <DialogHeader>
          <DialogTitle className="heading flex items-center gap-2"><MessageCircle size={18} className="text-indigo-600" /> New Chat</DialogTitle>
        </DialogHeader>
        <div className="flex gap-1.5 mt-2">
          {[{ k: "dm", l: "Direct Message" }, { k: "group", l: "Group" }].map(({ k, l }) => (
            <button
              key={k}
              type="button"
              onClick={() => { setMode(k); setSelected([]); }}
              className={`flex-1 h-9 text-[12px] font-semibold rounded-md border transition-all ${mode === k ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white border-slate-200 text-slate-700"}`}
              data-testid={`chat-new-mode-${k}`}
            >
              {l}
            </button>
          ))}
        </div>
        {mode === "group" && (
          <div className="mt-3">
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Group name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sales · Q1 push" className="mt-1.5 h-9 text-sm" data-testid="chat-new-group-name" />
          </div>
        )}
        <div className="mt-3">
          <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">{mode === "dm" ? "Person *" : "Members *"}</Label>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email…" className="mt-1.5 h-9 text-sm" data-testid="chat-new-search" />
          <div className="mt-2 max-h-64 overflow-y-auto rounded-md border border-slate-200">
            {filteredPeople.length === 0 && (<div className="text-center text-xs text-slate-400 italic py-6">No people found.</div>)}
            {filteredPeople.map((p) => (
              <label key={p.id} className={`flex items-center gap-2 px-2.5 py-1.5 border-b border-slate-100 text-[12.5px] hover:bg-slate-50 cursor-pointer ${selected.includes(p.id) ? "bg-indigo-50" : ""}`} data-testid={`chat-new-person-${p.id}`}>
                <input
                  type={mode === "dm" ? "radio" : "checkbox"}
                  checked={selected.includes(p.id)}
                  onChange={() => togglePerson(p.id)}
                  className="accent-indigo-600"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-800 truncate">{p.name}</div>
                  <div className="text-[10px] text-slate-500 truncate">{p.email}</div>
                </div>
              </label>
            ))}
          </div>
          {mode === "group" && selected.length > 0 && (
            <div className="text-[11px] text-slate-500 mt-1">{selected.length} member{selected.length === 1 ? "" : "s"} selected</div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="chat-new-submit">
            {busy ? "Creating…" : (mode === "dm" ? "Start Chat" : "Create Group")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
